import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { Trash2, Shield, Play, PowerOff, Bell, Calendar, HelpCircle, Loader2, KeyRound, AlertTriangle } from 'lucide-react';
import { DemoCredenciaisModal } from './DemoCredenciaisModal';


interface Municipio {
  id: string;
  nome: string;
  latitude?: number | null;
  longitude?: number | null;
  estado?: { sigla: string };
}


interface Escola {
  id: string;
  nome: string;
  isDemo?: boolean;
  demoAtiva?: boolean;
  demoCriadaEm?: string | null;
  demoExpiraEm?: string | null;
  enviarPushReais?: boolean;
  ativo: boolean;
}

interface DemoInstallWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onRefreshList: () => void;
  escolasExistentes: Escola[];
}

export function DemoInstallWizard({ isOpen, onClose, onRefreshList, escolasExistentes }: DemoInstallWizardProps) {
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);

  // Form de instalação
  const [selectedMunicipio, setSelectedMunicipio] = useState('');
  const [escolaId, setEscolaId] = useState('demo-escola-bh');
  const [nomeEscola, setNomeEscola] = useState('Escola Municipal Demo BH');
  const [diasValidade, setDiasValidade] = useState(7);
  const [feriadosLocais, setFeriadosLocais] = useState<string>(''); // formato comma-separated YYYY-MM-DD

  // Estado do Job
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [jobProgresso, setJobProgresso] = useState(0);
  const [jobMensagem, setJobMensagem] = useState('');
  const [jobLoading, setJobLoading] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [credenciaisEscola, setCredenciaisEscola] = useState<{ id: string; nome: string } | null>(null);

  // Carregar municípios
  useEffect(() => {
    if (isOpen) {
      setLoadingMunicipios(true);
      api.get('/admin/municipios')
        .then(r => {
          const list = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
          setMunicipios(list.filter((x: any) => x.ativo !== false));
          if (list.length > 0) setSelectedMunicipio(list[0].id);
        })
        .catch(() => toast.error('Erro ao carregar municípios para vinculação'))
        .finally(() => setLoadingMunicipios(false));
    }
  }, [isOpen]);

  // Polling de Status da Instalação
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(() => {
      api.get(`/admin/escola-demo/status/${jobId}`)
        .then(r => {
          const job = r.data;
          setJobProgresso(job.progresso);
          setJobStatus(job.status);
          setJobMensagem(job.mensagem);

          if (job.status === 'CONCLUIDO') {
            toast.success('Escola de Demonstração instalada com sucesso!');
            setJobId(null);
            setJobLoading(false);
            onRefreshList();
          } else if (job.status === 'ERRO') {
            toast.error(`Falha na instalação: ${job.erro || job.mensagem}`);
            setJobId(null);
            setJobLoading(false);
          }
        })
        .catch(() => {
          toast.error('Erro ao verificar progresso da instalação');
          setJobId(null);
          setJobLoading(false);
        });
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, onRefreshList]);

  if (!isOpen) return null;

  const munSelecionado = municipios.find(m => m.id === selectedMunicipio);
  const munSemGeo = munSelecionado && (!munSelecionado.latitude || !munSelecionado.longitude);

  const handleInstalar = async () => {
    if (!selectedMunicipio) {
      toast.error('Selecione um município parceiro.');
      return;
    }
    if (!escolaId.trim() || !nomeEscola.trim()) {
      toast.error('Informe o nome e ID da escola.');
      return;
    }

    setJobLoading(true);
    setJobProgresso(0);
    setJobStatus('INICIANDO');
    setJobMensagem('Enviando requisição de sementeira...');

    const feriadosArray = feriadosLocais.split(',')
      .map(x => x.trim())
      .filter(x => /^\d{4}-\d{2}-\d{2}$/.test(x));

    try {
      const response = await api.post('/admin/escola-demo/instalar', {
        municipioId: selectedMunicipio,
        escolaId: escolaId.trim(),
        nomeEscola: nomeEscola.trim(),
        diasValidadeDemo: Number(diasValidade),
        feriadosLocais: feriadosArray,
      });

      if (response.data?.jobId) {
        setJobId(response.data.jobId);
        toast.info('Instalação iniciada em segundo plano. Acompanhe a barra de progresso.');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erro ao disparar instalação.';
      toast.error(msg);
      setJobLoading(false);
    }
  };

  const handleToggleSwitch = async (escola: Escola, key: 'demoAtiva' | 'enviarPushReais', value: boolean) => {
    try {
      await api.post('/admin/escola-demo/configurar', {
        escolaId: escola.id,
        [key]: value
      });
      toast.success('Configuração atualizada com sucesso!');
      onRefreshList();
    } catch (err: any) {
      toast.error('Erro ao atualizar switches: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleExcluir = async (escola: Escola) => {
    const confirm = window.confirm(`Tem certeza que deseja excluir permanentemente a escola demo "${escola.nome}" e TODOS os seus dados históricos simulados?`);
    if (!confirm) return;

    setExcluindo(true);
    try {
      await api.delete(`/admin/escola-demo/excluir/${escola.id}`);
      toast.success('Escola de demonstração expurgada do banco com sucesso.');
      onRefreshList();
    } catch (err: any) {
      toast.error('Erro ao excluir escola demo: ' + (err.response?.data?.message || err.message));
    } finally {
      setExcluindo(false);
    }
  };

  const demoEscolas = escolasExistentes.filter(e => e.isDemo);

  return (
    <>
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      padding: 16
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 760,
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden',
        position: 'relative'
      }}>
        
        {/* Overlay de Exclusão (Loading & Block Screen) */}
        {excluindo && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(2px)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, borderRadius: 16
          }}>
            <Loader2 className="animate-spin" size={32} style={{ color: '#ef4444', marginBottom: 12 }} />
            <p style={{ margin: 0, fontSize: '.88rem', fontWeight: 800, color: '#1e293b' }}>
              Expurgando dados históricos...
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '.72rem', color: '#64748b' }}>
              Aguarde a conclusão da purga cirúrgica no banco de dados.
            </p>
          </div>
        )}
        
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={18} style={{ color: '#10b981' }} />
              Ambiente de Demonstração (Escola Demo)
            </h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '.72rem', color: '#94a3b8' }}>
              Instale, configure validade (TTL) e gerencie simuladores de frequência e acessos.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.25rem', cursor: 'pointer'
          }}>&times;</button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Barra de Progresso do Job Ativo */}
          {jobLoading && (
            <div style={{
              background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 12, padding: 16
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: '.78rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Loader2 className="animate-spin" size={14} />
                  Executando Instalação ({jobProgresso}%)
                </span>
                <span style={{ fontSize: '.72rem', color: '#64748b' }}>Status: {jobStatus}</span>
              </div>
              <div style={{ background: '#e2e8f0', height: 8, borderRadius: 999, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{ background: '#10b981', width: `${jobProgresso}%`, height: '100%', transition: 'width 0.3s ease' }} />
              </div>
              <p style={{ margin: 0, fontSize: '.72rem', color: '#475569', fontStyle: 'italic' }}>
                {jobMensagem || 'Gravando dados de simulação...'}
              </p>
            </div>
          )}

          {/* Form de Nova Instalação */}
          {!jobLoading && (
            <div style={{
              border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: '#f8fafc'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '.88rem', fontWeight: 700, color: '#0f172a' }}>
                Nova Escola de Demonstração
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '.7rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    MUNICÍPIO VINCULADO (PARCEIRO)
                  </label>
                  {loadingMunicipios ? (
                    <div style={{ fontSize: '.78rem', color: '#64748b' }}>Carregando...</div>
                  ) : (
                    <select
                      value={selectedMunicipio}
                      onChange={e => setSelectedMunicipio(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '.78rem', backgroundColor: '#ffffff', color: '#0f172a' }}
                    >
                      {municipios.map(m => (
                        <option key={m.id} value={m.id}>{m.nome} ({m.estado?.sigla})</option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '.7rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    PERÍODO DE VALIDADE (TTL)
                  </label>
                  <select
                    value={diasValidade}
                    onChange={e => setDiasValidade(Number(e.target.value))}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '.78rem', backgroundColor: '#ffffff', color: '#0f172a' }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map(d => (
                      <option key={d} value={d}>{d} {d === 1 ? 'Dia' : 'Dias'} (Expira em seguida)</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '.7rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    ID DA ESCOLA (ÚNICO)
                  </label>
                  <input
                    type="text"
                    value={escolaId}
                    onChange={e => setEscolaId(e.target.value)}
                    placeholder="demo-escola-bh"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '.78rem', backgroundColor: '#ffffff', color: '#0f172a' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '.7rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                    NOME DA ESCOLA
                  </label>
                  <input
                    type="text"
                    value={nomeEscola}
                    onChange={e => setNomeEscola(e.target.value)}
                    placeholder="Escola Municipal Demo BH"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '.78rem', backgroundColor: '#ffffff', color: '#0f172a' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '.7rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                  FERIADOS LOCAIS / MUNICIPAIS (OPCIONAL, SEPARADOS POR VÍRGULA YYYY-MM-DD)
                </label>
                <input
                  type="text"
                  value={feriadosLocais}
                  onChange={e => setFeriadosLocais(e.target.value)}
                  placeholder="2026-08-15, 2026-12-08"
                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '.78rem', backgroundColor: '#ffffff', color: '#0f172a' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                {munSemGeo && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)',
                    borderRadius: 8, fontSize: '.72rem', color: '#92400e', fontWeight: 600, flex: 1,
                  }}>
                    <AlertTriangle size={13} />
                    Este município ainda não tem coordenadas (lat/lng) cadastradas.
                    Registre a localização no cadastro do município antes de instalar a demo.
                  </div>
                )}
                <button
                  onClick={handleInstalar}
                  disabled={!!munSemGeo}
                  style={{
                    background: munSemGeo ? '#e2e8f0' : '#10b981',
                    color: munSemGeo ? '#94a3b8' : '#fff',
                    padding: '6px 16px', borderRadius: 8,
                    border: 'none', fontWeight: 700, fontSize: '.78rem',
                    cursor: munSemGeo ? 'not-allowed' : 'pointer',
                  }}
                  title={munSemGeo ? 'Cadastre as coordenadas do município primeiro' : undefined}
                >
                  Instalar / Semear Dados
                </button>
              </div>
            </div>
          )}

          {/* Listagem de Escolas Demo Cadastradas */}
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '.88rem', fontWeight: 700, color: '#0f172a' }}>
              Escolas de Demonstração Instaladas ({demoEscolas.length})
            </h3>
            
            {demoEscolas.length === 0 ? (
              <div style={{
                padding: '24px 16px', border: '1px dashed #cbd5e1', borderRadius: 12,
                textAlign: 'center', color: '#64748b', fontSize: '.78rem'
              }}>
                Nenhuma escola de demonstração ativa no sistema no momento.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {demoEscolas.map(escola => {
                  const dataExp = escola.demoExpiraEm ? new Date(escola.demoExpiraEm) : null;
                  const expiraSegundos = dataExp ? Math.round((dataExp.getTime() - Date.now()) / 1000) : 0;
                  const expiraDias = Math.ceil(expiraSegundos / (24 * 3600));

                  return (
                    <div key={escola.id} style={{
                      border: '1px solid #e2e8f0', borderRadius: 10, padding: 12,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: '#fff'
                    }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '.8rem', color: '#0f172a' }}>{escola.nome}</div>
                        <div style={{ fontSize: '.68rem', color: '#64748b', marginTop: 2 }}>ID: {escola.id}</div>
                        
                        {/* Exibição da data de expiração */}
                        {dataExp && (
                          <div style={{
                            fontSize: '.68rem', color: expiraSegundos <= 0 ? '#ef4444' : '#f59e0b',
                            display: 'flex', alignItems: 'center', gap: 4, marginTop: 4
                          }}>
                            <Calendar size={12} />
                            {expiraSegundos <= 0 
                              ? 'Demonstração Expirada (Inativa)' 
                              : `Validade: Expira em ${expiraDias} ${expiraDias === 1 ? 'dia' : 'dias'} (${dataExp.toLocaleDateString('pt-BR')})`}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        
                        {/* Botão Credenciais */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Acesso</span>
                          <button
                            onClick={() => setCredenciaisEscola({ id: escola.id, nome: escola.nome })}
                            title="Ver credenciais de acesso dos usuários demo"
                            style={{
                              background: 'rgba(99,102,241,0.08)', color: '#6366f1',
                              border: '1px solid rgba(99,102,241,0.2)',
                              borderRadius: 6, padding: '3px 8px', fontSize: '.68rem',
                              fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <KeyRound size={10} /> Credenciais
                          </button>
                        </div>

                        {/* Switch Simulador Ativo */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Simulação</span>
                          <button
                            onClick={() => handleToggleSwitch(escola, 'demoAtiva', !escola.demoAtiva)}
                            title={escola.demoAtiva
                              ? 'Escola em apresentação ativa — protegida contra limpeza automática. Clique para desativar.'
                              : '⚠️ Escola inativa — pode ser removida pelo reset do ambiente de dev. Ative para proteger durante apresentações.'}
                            style={{
                              background: escola.demoAtiva ? '#10b981' : '#e2e8f0',
                              color: escola.demoAtiva ? '#fff' : '#64748b',
                              border: escola.demoAtiva ? 'none' : '1px solid #f59e0b',
                              borderRadius: 6, padding: '3px 8px', fontSize: '.68rem',
                              fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                            }}
                          >
                            {escola.demoAtiva ? <Play size={10} /> : <PowerOff size={10} />}
                            {escola.demoAtiva ? 'Ativa' : 'Inativa'}
                          </button>
                        </div>

                        {/* Switch Push Reais */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Push Reais</span>
                          <button
                            onClick={() => handleToggleSwitch(escola, 'enviarPushReais', !escola.enviarPushReais)}
                            style={{
                              background: escola.enviarPushReais ? '#6366f1' : '#e2e8f0',
                              color: escola.enviarPushReais ? '#fff' : '#64748b',
                              border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: '.68rem',
                              fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <Bell size={10} />
                            {escola.enviarPushReais ? 'Real (FCM)' : 'Simulado'}
                          </button>
                        </div>

                        {/* Botão Excluir */}
                        <button
                          onClick={() => handleExcluir(escola)}
                          style={{
                            background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)',
                            borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}
                          title="Excluir permanentemente — remove escola e todos os dados históricos simulados do banco"
                        >
                          <Trash2 size={14} />
                        </button>

                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #cbd5e1',
              color: '#334155', background: '#fff', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer'
            }}
          >
            Fechar Painel
          </button>
        </div>

      </div>
    </div>

    {credenciaisEscola && (
      <DemoCredenciaisModal
        escolaId={credenciaisEscola.id}
        escolaNome={credenciaisEscola.nome}
        isOpen={true}
        onClose={() => setCredenciaisEscola(null)}
      />
    )}
    </>
  );
}

// Re-export modal for use in other pages
export { DemoCredenciaisModal } from './DemoCredenciaisModal';

